import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import ChevronIcon from 'components/ReusableComponents/Icons/Chevron'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { useGetSavedSmartDatepickerDates } from 'components/ReusableComponents/SmartDatepicker'
import AnalyticTabs from 'features/Analytics/AnalyticTab'
import Conversions from 'features/Analytics/Conversions'
import OTAOutreach from 'features/Analytics/OTAOutreach'
import Outreach from 'features/Analytics/Outreach'
import Reviews from 'features/Analytics/Reviews'
import UpArrow from 'features/Analytics/UpArrow'
import MaintenancePage from 'features/Maintenance'
import SubscriptionAnalyticsNew from 'features/SubscriptionSnowflake'
import useAllHotelsToggle, { AnalyticsContextWrapper } from 'hooks/useAllHotels'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Link, Route, Switch, useLocation } from 'react-router-dom'
import performanceReportsApi from 'services/api/performanceReports'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import styles from './Analytics.module.scss'

const analyticCardColors = {
  ceruleanBlue: '#2B6CB0',
  cornFlowerBlue: '#667EEA',
  mediumPurple: '#9B51E0',
  coolBlue: '#288DA4',
  goldenGrass: '#E2A62C',
  ocenGreen: '#3AAF71'
}

const pageNames = {
  outreach: 'Outreach',
  reviews: 'Reviews',
  conversions: 'Conversions',
  'OTA-outreach': 'OTA Outreach',
  //engagement: 'Engagement',
  subscriptions: 'Subscriptions'
}

function getPageNameFromLocation(location) {
  const currentPathName = location.pathname.match(/(?<=\/analytics\/)[^/]+/)

  if (currentPathName) {
    return pageNames[currentPathName]
  }
}

const Card = ({ number, delta, fromDate, toDate, label, buttonText, path, color }) => (
  <Link
    to={path}
    className={styles.card}
    style={{ backgroundColor: color || 'gray', borderColor: color }}
  >
    <p className={styles.cardLabel}>{label}</p>
    <p className={styles.cardNumber}>{formatNumberAsThousands(number)}</p>
    <div className={styles.deltaHolder}>
      <UpArrow />{' '}
      <span className={styles.deltaNumber}>{formatNumberAsThousands(delta)}</span>{' '}
      <span className={styles.deltaDate}>
        {' '}
        {moment(fromDate).format('MMM D')} - {moment(toDate).format('MMM D')}
      </span>
    </div>
    <button className={styles.cardButton} style={{ color }}>
      {buttonText}
    </button>
  </Link>
)

export const usePerformanceReportsDates = () => {
  const localStorageKey = 'performance-reports'
  const savedSmartDatepickerDates = useGetSavedSmartDatepickerDates(localStorageKey)

  const [fromDate, setFromDate] = useState(
    savedSmartDatepickerDates?.[0]?.format('YYYY-MM-DD') ||
      moment().add(-14, 'days').format('YYYY-MM-DD')
  )

  const [toDate, setToDate] = useState(
    savedSmartDatepickerDates?.[1]?.format('YYYY-MM-DD') || moment().format('YYYY-MM-DD')
  )

  return { fromDate, setFromDate, toDate, setToDate }
}

const Analytics = () => {
  const { fromDate, setFromDate, toDate, setToDate } = usePerformanceReportsDates()

  const location = useLocation()

  const {
    showAllHotels,
    setShowAllHotels,
    showWholeGroup,
    setShowWholeGroup,
    parentChain,
    setParentChain,
    idsOfCurrentGroup,
    idsOfAllHotels,
    isAllTime,
    setIsAllTime
  } = useAllHotelsToggle()

  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState()

  const [overviewData, setOverviewData] = useState()

  useEffect(() => {
    const abortController = new AbortController()

    let kind = 'CurrentSubsidiary'

    if (showAllHotels) {
      kind = 'AllSubsidiaries'
    }

    if (showWholeGroup) {
      kind = 'WholeGroup'
    }

    performanceReportsApi
      .overview(
        {
          fromDate,
          toDate,
          kind,
          parentChainCode: parentChain,
          isAllTheTime: isAllTime
        },
        abortController
      )
      .then(res => {
        setIsErred(false)
        setOverviewData(res)
      })
      .catch(err => {
        if (!err.toString().includes('The user aborted a request.')) {
          setIsErred(true)
        }
      })
      .finally(() => {
        setIsLoading(false)
      })

    return () => abortController.abort()
  }, [
    fromDate,
    toDate,
    showAllHotels,
    showWholeGroup,
    idsOfCurrentGroup,
    idsOfAllHotels,
    parentChain,
    isAllTime
  ])

  const pageName = getPageNameFromLocation(location)

  const MAINTENANCE_MODE = false

  if (MAINTENANCE_MODE) {
    return (
      <div>
        <MaintenancePage hasWrapper={false} />
      </div>
    )
  }

  return (
    <div>
      <CustomHeader width={1180}>
        {pageName ? (
          <div className={styles.header}>
            <Link to="/analytics">Analytics</Link>
            <ChevronIcon /> {pageName}
          </div>
        ) : (
          <div className={styles.header}>Analytics</div>
        )}
      </CustomHeader>
      <Switch>
        <Route path="/analytics/subscriptions">
          <SubscriptionAnalyticsNew showTabs={true} />
        </Route>
        <Route path="/analytics/outreach">
          <Outreach />
        </Route>
        <Route path="/analytics/ota-outreach">
          <OTAOutreach />
        </Route>
        <Route path="/analytics/conversions">
          <Conversions />
        </Route>
        <Route path="/analytics/reviews">
          <Reviews />
        </Route>
        {/* <Route path="/analytics/engagement">
          <Engagement />
        </Route> */}
        <Route exact path="/analytics">
          <div className={styles.datepickerHolder}>
            <AnalyticTabs
              showTabs={false}
              showWholeGroup={showWholeGroup}
              setShowWholeGroup={setShowWholeGroup}
              setShowAllHotels={setShowAllHotels}
              showAllHotels={showAllHotels}
              setFromDate={setFromDate}
              setToDate={setToDate}
              fromDate={fromDate}
              toDate={toDate}
              parentChain={parentChain}
              setParentChain={setParentChain}
              isAllTime={isAllTime}
              setIsAllTime={setIsAllTime}
            />
          </div>
          <DataIsReprocessingBar />
          {isErred && (
            <div className={styles.noResults}>
              <p>Something went wrong while getting outreach data</p>
            </div>
          )}
          {!isErred && isLoading && <LoadingIndicator />}
          {!isErred && !isLoading && (
            <div className={styles.cardsHolder}>
              <Card
                number={overviewData?.subscriberSizeTotal}
                delta={overviewData?.subscriberSize}
                label="Subscribers"
                buttonText="Go to Subscriptions"
                color={analyticCardColors.ceruleanBlue}
                path="/analytics/subscriptions"
                fromDate={fromDate}
                toDate={toDate}
              />
              <Card
                number={overviewData?.outReachesTotal}
                delta={overviewData?.outReaches}
                label="Guests reached"
                buttonText="Go to Outreach"
                color={analyticCardColors.cornFlowerBlue}
                path="/analytics/outreach"
                fromDate={fromDate}
                toDate={toDate}
              />
              <Card
                number={overviewData?.otaOutReachesTotal}
                delta={overviewData?.otaOutReaches}
                label="OTA guests reached"
                buttonText="Go to OTA Outreach"
                color={analyticCardColors.mediumPurple}
                path="/analytics/OTA-outreach"
                fromDate={fromDate}
                toDate={toDate}
              />
              <Card
                number={overviewData?.tripAdvisorReviewsTotal}
                delta={overviewData?.tripAdvisorReviews}
                label="TripAdvisor reviews"
                buttonText="Go to Reviews"
                color={analyticCardColors.coolBlue}
                path="/analytics/reviews"
                fromDate={fromDate}
                toDate={toDate}
              />
              {/* <Card
                number={overviewData?.guestsEngagedTotal}
                delta={overviewData?.guestsEngaged}
                label="Guests engaged"
                buttonText="Go to Engagement"
                color={analyticCardColors.goldenGrass}
                path="/analytics/engagement"
                fromDate={fromDate}
                toDate={toDate}
              /> */}
              <Card
                number={overviewData?.bookingsTotal}
                delta={overviewData?.bookings}
                label="Directful bookings"
                buttonText="Go to Conversions"
                color={analyticCardColors.ocenGreen}
                path="/analytics/conversions"
                fromDate={fromDate}
                toDate={toDate}
              />
            </div>
          )}
        </Route>
      </Switch>
    </div>
  )
}

const AnalyticsWithWrapper = () => (
  <AnalyticsContextWrapper>
    <Analytics />
  </AnalyticsContextWrapper>
)

export default AnalyticsWithWrapper
